import dayjs from 'utils/customDayJS';
import {
	updatePreviousPositions,
	formatAirmeshDataIntoFeatures,
	formatAirmeshName
} from 'components/airSpace/airspaceUtils';
import { TRAILS_MIN_ZOOM, TRAILS_MAX_ZOOM } from 'components/airSpace/airspaceConstants';
import {
	setAllFilterTypes,
	airMeshAircraftTypesSelector,
	airMeshProviderTypesSelector,
	airMeshSignalTypesSelector,
	airMeshSignalProviderTypesSelector,
	setAMTrafficFeatures,
	pauseTrafficUpdatesSelector
} from 'reducers/liveTrafficSlice';

// Helper to normalize provider names (removes _12 suffix from airmesh_sdk_12)
const removeIntFromProvider = string => {
	if (!string) return string;
	return string.replace(/_\d+/g, '');
};

// Constants for feature state management
export const HIDE_ASTERIX_THRESHOLD_SECONDS = 10;
export const EXPIRE_ASTERIX_THRESHOLD_MINUTES = 1;
export const EXPIRE_REGULAR_THRESHOLD_SECONDS = 30;
export const EXPIRE_ADSB_THRESHOLD_SECONDS = 60;
export const INACTIVITY_TIMEOUT_MS = 30000;

// Feature state types
export const FEATURE_STATES = {
	EXPIRED: 'EXPIRED',
	VISIBLE: 'VISIBLE',
	HIDDEN: 'HIDDEN'
};

// Map to store hidden features
const hiddenFeaturesMap = new Map();

// Helper function to check if feature has invalid coordinates [0,0,x] or missing geometry
export const hasInvalidCoordinates = feature => {
	if (!feature.geometry || !feature.geometry.coordinates) {
		return true;
	}
	const [x, y] = feature.geometry.coordinates;
	return x === 0 && y === 0;
};

// Helper function to determine feature state
export const getFeatureState = (feature, now) => {
	const featureDate = dayjs(feature.properties.timestamp);
	const objectType = feature.properties.object_type;
	const signalType = feature.properties.signal_type;
	const isAsterix = objectType === 'asterix';
	const isADSB = signalType === 'ADS-B';

	// Use float seconds so the threshold behaves as expected (no integer rounding surprises).
	const timeSinceUpdateSeconds = now.diff(featureDate, 'second', true);

	// Determine expiration threshold:
	// - Asterix: EXPIRE_ASTERIX_THRESHOLD_MINUTES (minutes)
	// - ADS-B: EXPIRE_ADSB_THRESHOLD_SECONDS (seconds)
	// - All other: EXPIRE_REGULAR_THRESHOLD_SECONDS (seconds, current default 30)
	const expireCutoff = isAsterix
		? now.subtract(EXPIRE_ASTERIX_THRESHOLD_MINUTES, 'minutes')
		: now.subtract(
				isADSB ? EXPIRE_ADSB_THRESHOLD_SECONDS : EXPIRE_REGULAR_THRESHOLD_SECONDS,
				'seconds'
			);

	const isExpired = featureDate <= expireCutoff;

	if (isExpired) return FEATURE_STATES.EXPIRED;
	if (!isAsterix) return FEATURE_STATES.VISIBLE;
	return timeSinceUpdateSeconds > HIDE_ASTERIX_THRESHOLD_SECONDS
		? FEATURE_STATES.HIDDEN
		: FEATURE_STATES.VISIBLE;
};

// Helper function to update feature with previous data
export const updateFeatureWithPrevious = (
	newFeature,
	previousFeature,
	shouldCalculateTrails = true
) => {
	const newAltitude = newFeature.properties?.altitude;
	const previousAltitude = previousFeature.properties?.altitude;
	const altitudeChange = newAltitude && previousAltitude ? newAltitude - previousAltitude : 0;

	// Only calculate previous positions if trails are visible (zoom 9-13, TRAILS_MIN_ZOOM–TRAILS_MAX_ZOOM)
	// Clear trails when zoomed out beyond visible range
	const previousPositions = shouldCalculateTrails
		? updatePreviousPositions(previousFeature, newFeature)
		: [];

	// Build updated properties object
	const updatedProperties = Object.assign({}, newFeature.properties, {
		altitude_change: altitudeChange,
		previous_positions: previousPositions
	});

	return Object.assign({}, newFeature, {
		properties: updatedProperties
	});
};

// Helper function to check if a point is within bounding box
const isPointInBounds = (coordinates, boundingBox) => {
	if (!boundingBox) return true; // No bounds = include everything
	const [x, y] = coordinates;
	const [minX, minY, maxX, maxY] = boundingBox;
	return x >= minX && x <= maxX && y >= minY && y <= maxY;
};

/**
 * Step 1: Filter and Format Incoming Data
 */
const filterAndFormatIncomingData = (rawFeatures, boundingBox, now) => {
	// Filter raw data: ordered by cost/benefit ratio (eliminate most data with cheapest checks first)
	const filteredRawData = rawFeatures.filter(feature => {
		// 1. Filter invalid coordinates (fastest check, prevents errors)
		if (!feature.geometry || !feature.geometry.coordinates) {
			return false;
		}
		const [x, y] = feature.geometry.coordinates;
		if (x === 0 && y === 0) {
			return false;
		}

		// 2. Filter by bounds (fast, eliminates MOST features when zoomed in)
		if (boundingBox && !isPointInBounds(feature.geometry.coordinates, boundingBox)) {
			return false;
		}

		// 3. Filter expired features (most expensive - dayjs parsing, but now runs on smallest set)
		const featureDate = dayjs(feature.observed_timestamp);
		const timeSinceUpdateSeconds = now.diff(featureDate, 'seconds');

		// Use different expiration thresholds by signal type (raw API uses lowercase e.g. 'adsb'):
		// - ADS-B: EXPIRE_ADSB_THRESHOLD_SECONDS (seconds)
		// - All others: EXPIRE_REGULAR_THRESHOLD_SECONDS (current default 30)
		const signalType = feature.signal_type ?? feature.signal?.type;
		const expireThresholdSeconds =
			signalType === 'adsb' ? EXPIRE_ADSB_THRESHOLD_SECONDS : EXPIRE_REGULAR_THRESHOLD_SECONDS;

		if (timeSinceUpdateSeconds >= expireThresholdSeconds) {
			return false;
		}

		return true;
	});

	// Format the filtered data into GeoJSON features
	return formatAirmeshDataIntoFeatures(filteredRawData);
};

/**
 * Step 2: Prepare Feature Maps
 * Creates maps for efficient lookups during merge process
 */
const prepareFeatureMaps = (formattedIncomingFeatures, currentFeatures, boundingBox, now) => {
	// Map incoming features by ID for fast lookup
	const incomingFeaturesMap = new Map(
		formattedIncomingFeatures.map(feature => [feature.id, feature])
	);

	// Filter current features: remove expired and out-of-bounds
	const currentFeaturesMap = new Map(
		currentFeatures
			.filter(feature => getFeatureState(feature, now) !== FEATURE_STATES.EXPIRED)
			.filter(feature => !boundingBox || isPointInBounds(feature.geometry.coordinates, boundingBox))
			.map(feature => [feature.id, feature])
	);

	// Clean up expired hidden asterix features
	for (const [id, feature] of hiddenFeaturesMap) {
		if (getFeatureState(feature, now) === FEATURE_STATES.EXPIRED) {
			hiddenFeaturesMap.delete(id);
		}
	}

	return { incomingFeaturesMap, currentFeaturesMap };
};

/**
 * Step 3: Merge Feature Data
 * Merges incoming features with existing data (for trails, altitude changes, etc.)
 */
const mergeFeatureData = (
	incomingFeature,
	currentFeature,
	hiddenFeature,
	shouldCalculateTrails
) => {
	if (incomingFeature) {
		// New data available - merge with previous data for trails/altitude
		const previousFeature = currentFeature || hiddenFeature;
		return previousFeature
			? updateFeatureWithPrevious(incomingFeature, previousFeature, shouldCalculateTrails)
			: incomingFeature;
	} else {
		// No new data - keep existing feature
		return currentFeature || hiddenFeature;
	}
};

/**
 * Step 4: Apply Visibility Rules
 * Determines if a feature should be visible based on type and state
 * - Asterix: can be VISIBLE, HIDDEN, or EXPIRED
 * - Others: always VISIBLE unless EXPIRED
 */
const applyVisibilityRules = (feature, now, processedFeatures) => {
	const state = getFeatureState(feature, now);
	const isAsterix = feature.properties.object_type === 'asterix';
	const id = feature.id;

	if (isAsterix) {
		switch (state) {
			case FEATURE_STATES.VISIBLE:
				processedFeatures.set(id, feature);
				hiddenFeaturesMap.delete(id);
				break;
			case FEATURE_STATES.HIDDEN:
				hiddenFeaturesMap.set(id, feature);
				break;
			case FEATURE_STATES.EXPIRED:
				// Remove from maps, don't include in output
				hiddenFeaturesMap.delete(id);
				break;
			default:
				break;
		}
	} else {
		// Non-asterix features: visible unless expired
		if (state !== FEATURE_STATES.EXPIRED) {
			processedFeatures.set(id, feature);
		}
	}
};

/**
 * Main Feature Update Pipeline
 *
 * Flow:
 * 1. Filter and format incoming raw data (viewport + time filtering)
 * 2. Prepare feature maps (incoming, current, hidden)
 * 3. Merge all features together (new data + historical data for trails)
 * 4. Apply visibility rules (asterix hiding, expiration)
 * 5. Return final feature list for Redux
 */
export const updateFeatures = (
	rawIncomingFeatures,
	currentFeatures,
	boundingBox = null,
	mapZoom = null
) => {
	const now = dayjs();

	// Log zoom visibility for testing
	// logZoomVisibility(mapZoom);

	// Determine if trails should be calculated based on zoom
	const shouldCalculateTrails =
		mapZoom !== null && mapZoom >= TRAILS_MIN_ZOOM && mapZoom <= TRAILS_MAX_ZOOM;

	// Step 1: Filter and format incoming data
	const formattedIncomingFeatures = filterAndFormatIncomingData(
		rawIncomingFeatures,
		boundingBox,
		now
	);

	// Step 2: Prepare feature maps for efficient merging
	const { incomingFeaturesMap, currentFeaturesMap } = prepareFeatureMaps(
		formattedIncomingFeatures,
		currentFeatures,
		boundingBox,
		now
	);

	// Step 3 & 4: Merge features and apply visibility rules
	const processedFeatures = new Map();

	// Get all unique feature IDs from all sources
	const allFeatureIds = new Set([
		...incomingFeaturesMap.keys(),
		...currentFeaturesMap.keys(),
		...hiddenFeaturesMap.keys()
	]);

	// Process each feature
	for (const id of allFeatureIds) {
		const incomingFeature = incomingFeaturesMap.get(id);
		const currentFeature = currentFeaturesMap.get(id);
		const hiddenFeature = hiddenFeaturesMap.get(id);

		// Skip if no feature data available
		if (!incomingFeature && !currentFeature && !hiddenFeature) continue;

		// Merge feature data
		const mergedFeature = mergeFeatureData(
			incomingFeature,
			currentFeature,
			hiddenFeature,
			shouldCalculateTrails
		);

		if (!mergedFeature) continue;

		// Apply visibility rules
		applyVisibilityRules(mergedFeature, now, processedFeatures);
	}

	return [...processedFeatures.values()];
};

/**
 * Update Filter Dropdown Options
 *
 * Extracts unique values for filter dropdowns (aircraft types, providers, etc.)
 * Only processes NEW incoming features, not all existing features
 */
const isValidFilterOption = value => value != null && value !== '' && value !== 'N/A';

export const updateFilterTypesFromNewFeatures = rawNewFeatures => (dispatch, getState) => {
	// Extract types from NEW raw features; use formatAirmeshName so filter values match formatted feature properties
	const newAircraftTypes = rawNewFeatures.map(f => f.object_type).filter(isValidFilterOption);
	const newProviderTypes = rawNewFeatures
		.map(f => formatAirmeshName(removeIntFromProvider(f.provider)))
		.filter(isValidFilterOption);
	const newSignalTypes = rawNewFeatures
		.map(f => formatAirmeshName(f.signal?.type))
		.filter(isValidFilterOption);
	const newSignalProviderTypes = rawNewFeatures
		.map(f => formatAirmeshName(f.signal?.provider))
		.filter(isValidFilterOption);

	// Only dispatch if we have new types
	if (
		newAircraftTypes.length > 0 ||
		newProviderTypes.length > 0 ||
		newSignalTypes.length > 0 ||
		newSignalProviderTypes.length > 0
	) {
		// Get existing types
		const state = getState();
		const existingAircraftTypes = airMeshAircraftTypesSelector(state);
		const existingProviderTypes = airMeshProviderTypesSelector(state);
		const existingSignalTypes = airMeshSignalTypesSelector(state);
		const existingSignalProviderTypes = airMeshSignalProviderTypesSelector(state);

		// Merge and deduplicate
		const mergedAircraftTypes = [...new Set([...existingAircraftTypes, ...newAircraftTypes])];
		const mergedProviderTypes = [...new Set([...existingProviderTypes, ...newProviderTypes])];
		const mergedSignalTypes = [...new Set([...existingSignalTypes, ...newSignalTypes])];
		const mergedSignalProviderTypes = [
			...new Set([...existingSignalProviderTypes, ...newSignalProviderTypes])
		];

		// Batch dispatch all types at once (1 action instead of 4)
		dispatch(
			setAllFilterTypes({
				aircraftTypes: mergedAircraftTypes,
				providerTypes: mergedProviderTypes,
				signalTypes: mergedSignalTypes,
				signalProviderTypes: mergedSignalProviderTypes
			})
		);
	}
};

let inactivityTimeout = null;

const resetInactivityTimer = dispatch => {
	// Clear any existing timeout
	if (inactivityTimeout) {
		clearTimeout(inactivityTimeout);
	}

	// Set a new timeout
	inactivityTimeout = setTimeout(() => {
		//Clear all features if there has been no update on any channel for 30 seconds
		//Handles the case where no events are received to trigger the regular pruning of old features
		dispatch(setAMTrafficFeatures([]));
	}, INACTIVITY_TIMEOUT_MS);
};

/**
 * Main Entry Point - Process Live Traffic Data
 * Called when new data arrives from SSE connection
 */
export const processData = data => (dispatch, getState) => {
	// Reset 30-second inactivity timer
	resetInactivityTimer(dispatch);

	// Don't update if user is interacting with table (dragging column headers) or zooming the map
	const pauseTrafficUpdates = pauseTrafficUpdatesSelector(getState());
	if (pauseTrafficUpdates) return;

	const state = getState();
	// Filter strictly to the visible viewport.
	const mapBounds = state.map?.mapBoundingBox || null;
	const mapZoom = state.map?.mapZoom || null;
	const currentFeatures = state.liveTraffic.airMeshFeatures;
	const updatedFeatures = updateFeatures(data, currentFeatures, mapBounds, mapZoom);

	// Dispatch to Redux
	dispatch(setAMTrafficFeatures(updatedFeatures));

	// Update filter dropdown options (aircraft types, providers, etc.)
	dispatch(updateFilterTypesFromNewFeatures(data));
};
